import { Component, OnInit, inject, signal, WritableSignal, ElementRef, ViewChild, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgxPaginationModule } from 'ngx-pagination';
import { finalize, single } from 'rxjs';

import { LmsApiService } from 'src/app/service/lms/lms-api-service';
import { DownloadService } from 'src/app/utils/download/download.service';
import { PageSize } from 'src/app/enumeration/pageSize';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// Interface definitions to eliminate 'any' type leaks
export interface LmsCouponCode {
  srNo: number;
  serialNumber: string;
  couponCode: string;
  courseCatalog: string;
  points: number;
  enabled: boolean;
}

export interface LMSApiResponse {
  Message?: {
    lmsCouponCodeRespLst?: LmsCouponCode[];
    allTotal?: number;
  };
}

@Component({
  selector: 'app-lms-coupon-code-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    NgxPaginationModule
  ],
  templateUrl: './lms-coupon-code-list.html',
  styleUrl: './lms-coupon-code-list.css',
})
export class LmsCouponCodeList implements OnInit {

  private readonly apiService = inject(LmsApiService);
  private readonly downloadService = inject(DownloadService);
  private readonly destroyRef = inject(DestroyRef);

  readonly courseCatalogNameList = signal<any[]>([])

  loading = signal<boolean>(false);
  downloading = signal<boolean>(false);
  isgenerateLoading = signal<boolean>(false);
  isCouponCodeEnableDisableAccess = signal<boolean>(true);

  @ViewChild('downloadCSVModal') downloadCSVModal!: ElementRef;

  errorToast = signal<string | null>(null);

  page = signal<number>(0);
  pageSize = signal<number>(PageSize.DEFAULT_LIST);
  allTotal = signal<number>(0);

  downloadFileName = '';
  startSerialNumber = '';
  endSerialNumber = '';

  lmsCouponCodesLst: LmsCouponCode[] = [];
  isEnableBtnLoading: WritableSignal<boolean>[] = []; // Track array buttons as an array of independent state signals
  couponCount: number | null = null;
  couponPoints: number | null = null;
  selectedCatalogName: any;

  ngOnInit(): void {
    this.getLMSCouponCodeList();
    this.fetchCourseCatalogNameList();
  }

  fetchCourseCatalogNameList() {

    this.apiService.getLMSCourseCatalogNameList()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (v: any) => {
          this.courseCatalogNameList.set(v?.Message || []);
        },
        error: (err: any) => {
          this.errorToast.set(err || 'Network error occurred while fetching catalog records.');
        }
      });
  }
  
  getLMSCouponCodeList(): void {
    this.loading.set(true);
    this.apiService.getLMSCouponCodeList(this.page()).pipe(
      finalize(() => this.loading.set(false))
    ).subscribe({
      next: (response: LMSApiResponse) => {
        this.lmsCouponCodesLst = response?.Message?.lmsCouponCodeRespLst ?? [];
        this.allTotal.set(response?.Message?.allTotal ?? 0);

        // Dynamic generation of reactive signal tracking for action load indicators
        this.isEnableBtnLoading = this.lmsCouponCodesLst.map(() => signal<boolean>(false));
      },
      error: (error) => {
        const errorMsg = error?.Object?.statusText || error?.statusText || error?.message || error || 'Error fetching LMS coupon codes.';
        this.errorToast.set(errorMsg);
        console.error('Error fetching LMS coupon codes:', error);
      }
    });
  }

  generateCouponCode(): void {
    if (!this.couponCount || !this.couponPoints || !this.selectedCatalogName) return;

    this.isgenerateLoading.set(true);
    this.apiService.getCreateLMSCouponCode(this.couponCount, this.couponPoints, this.selectedCatalogName).pipe(
      finalize(() => this.isgenerateLoading.set(false))
    ).subscribe({
      next: () => {
        this.couponCount = null;
        this.couponPoints = null;
        this.selectedCatalogName = null;
        this.getLMSCouponCodeList();
      },
      error: (error) => {
        const errorMsg = error?.Object?.statusText || error?.statusText || error?.message || error || 'Failed to generate coupons.';
        this.errorToast.set(errorMsg);
        console.error('Failed to generate coupons:', error)
      }
    });
  }

  enableLMSCouponCode(serialNumber: string, enabled: boolean, index: number): void {
    if (this.isEnableBtnLoading[index]) {
      this.isEnableBtnLoading[index].set(true);
    }

    this.apiService.getEnabledDisabledLMSCouponCode(serialNumber, !enabled).pipe(
      finalize(() => {
        if (this.isEnableBtnLoading[index]) this.isEnableBtnLoading[index].set(false);
      })
    ).subscribe({
      next: () => this.getLMSCouponCodeList(),
      error: (error) => {
        const errorMsg = error?.Object?.statusText || error?.statusText || error?.message || error || 'Failed to update coupon code.';
        this.errorToast.set(errorMsg);
        console.error('Failed to update coupon code:', error)
      }
    });
  }

  downloadFile(): void {
    if (!this.downloadFileName?.trim()) return;

    this.downloading.set(true);
    const fileName = this.downloadFileName.endsWith('.csv') ? this.downloadFileName : `${this.downloadFileName}.csv`;

    this.apiService.getLMSCouponCodeDownloadFile(fileName, this.startSerialNumber, this.endSerialNumber).pipe(
      finalize(() => this.downloading.set(false))
    ).subscribe({
      next: (file) => {
        this.downloadService.downloadFileInLocal(file, fileName);
        this.downloadFileName = '';
        this.closeModalProgrammatically();
      },
      error: (error) => {
        this.downloadFileName = '';
        const errorMsg = error?.Object?.statusText || error?.statusText || error?.message || error || 'Failed to download file.';
        this.errorToast.set(errorMsg);
        console.error('Failed to download file:', errorMsg);
      }
    });
  }

  handlePageChange(pageNumber: number): void {
    if (this.loading()) return;
    this.page.set(pageNumber - 1);
    this.getLMSCouponCodeList();
  }

  private closeModalProgrammatically(): void {
    // Pure TypeScript solution to cleanly interact with native Bootstrap modal instances without jQuery
    const modalElement = this.downloadCSVModal?.nativeElement;
    if (modalElement) {
      const bootstrapWindow = (window as any).bootstrap;
      if (bootstrapWindow?.Modal) {
        const modalInstance = bootstrapWindow.Modal.getInstance(modalElement) || new bootstrapWindow.Modal(modalElement);
        modalInstance.hide();
      }
    }
  }
  
}
